import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// The report designer's live canvas (TASK-133). Serialises the surrounding
// form, POSTs it to the render endpoint, and swaps the resulting PDF into the
// iframe. "Real time" is handled honestly: changes debounce ~800ms, a
// superseded render is aborted, and the last good page dims rather than
// blanks while its replacement is prepared.
export default class extends Controller {
  static targets = ["form", "themePickForm", "frame", "status", "retry", "canvas", "nameInput",
                    "sourceList", "sectionList", "conditional", "sheetPreview", "columnGroup", "formatsError",
                    "columnsMeta"]

  static values = {
    renderUrl: String,
    sourcesUrl: String,
    uploadUrl: String,
    sheetPreviewUrl: String,
    // "pdf" (default) renders into the iframe; "sheet" (TASK-229.5) renders
    // an HTML fragment into sheetPreviewTarget instead — same debounce/abort
    // machinery, different transport, so #render() branches on it rather
    // than duplicating the whole controller.
    previewKind: { type: String, default: "pdf" },
    debounce: { type: Number, default: 800 }
  }

  connect() {
    // Levels with nothing to preview against (the QTAG theme has no
    // submission; a brand-new project may have none yet, TASK-225.3) render
    // no canvas/frame at all — every render()/retry() call below no-ops via
    // hasFrameTarget rather than assuming one exists.

    // Drag-to-reorder the Content rows (TASK-134.3). The hidden order inputs
    // follow DOM order, so a drop just needs to trigger the debounced render
    // — the order is ephemeral until Save, like every other control.
    if (this.hasSectionListTarget) {
      this.sortable = Sortable.create(this.sectionListTarget, {
        handle: "[data-sortable-handle]",
        animation: 150,
        ghostClass: "opacity-50",
        onEnd: () => this.changed()
      })
    }

    // Spreadsheet mode's Columns accordion (TASK-229.5): one Sortable per
    // GROUP ("Submission details", then each section) — dragging reorders
    // within a group only, and since every group's checkboxes share the name
    // `template[sheet_columns][]`, the groups' own fixed order plus each
    // one's current DOM order is exactly the array the form submits (a
    // checkbox that's unticked simply doesn't post, so unlike the Sections
    // control this needs no separate hidden "all"/"visible" bookkeeping).
    if (this.hasColumnGroupTarget) {
      this.columnSortables = this.columnGroupTargets.map((group) => Sortable.create(group, {
        handle: "[data-sortable-handle]",
        animation: 150,
        ghostClass: "opacity-50",
        onEnd: () => this.changed()
      }))
    }

    this.syncConditionals()
    this.render()
  }

  disconnect() {
    this.sortable?.destroy()
    this.columnSortables?.forEach((sortable) => sortable.destroy())
    clearTimeout(this.timer)
    this.abortController?.abort()
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl)
  }

  // Any control in the rail (or the source radios) changed. input and change
  // both route here; the debounce collapses a colour-picker drag into one
  // render.
  changed() {
    this.syncConditionals()
    this.updateColumnsMeta()
    clearTimeout(this.timer)
    this.status("Waiting for you to pause…")
    this.timer = setTimeout(() => this.render(), this.debounceValue)
  }

  // The Columns accordion's "N of M" header (TASK-229.5) — recomputed
  // client-side on every change so ticking/unticking (or All/None) reads
  // correctly without waiting on the debounced preview round trip.
  updateColumnsMeta() {
    if (!this.hasColumnsMetaTarget || !this.hasColumnGroupTarget) return

    const boxes = this.columnGroupTargets.flatMap((group) => Array.from(group.querySelectorAll('input[type="checkbox"]')))
    const checked = boxes.filter((box) => box.checked).length
    this.columnsMetaTarget.textContent = `${checked} of ${boxes.length}`
  }

  // Progressive disclosure (TASK-134.5): blocks marked
  // data-show-when="branding_profile[header_layout]:mark,centred" show only
  // while the named control holds one of the listed values. Hidden blocks
  // still post — the layout radio, not the block's presence, decides what
  // the renderer reads.
  //
  // getAll() rather than get() (TASK-232.4): a radio group posts exactly
  // one value, so this behaves identically to the old get()-based check for
  // header_layout/footer_layout. A checkbox-backed field (the theme rail's
  // Cover sheet toggle, branding_profile[include_cover_sheet]) posts the
  // hidden "0" sentinel FIRST and, only when ticked, the real "1" SECOND —
  // get() always returns the first entry ("0"), so a checked box would read
  // as unchecked. getAll() sees both and asks whether the WANTED value is
  // present anywhere in the list, which is correct for both shapes.
  syncConditionals() {
    if (!this.hasConditionalTarget) return

    const data = new FormData(this.formTarget)
    this.conditionalTargets.forEach((block) => {
      const [field, values] = block.dataset.showWhen.split(":")
      const wanted = values.split(",")
      const present = data.getAll(field).map(String)
      block.hidden = !wanted.some((value) => present.includes(value))
    })
  }

  // In-studio asset upload (TASK-134.5): the header mark or a partner logo.
  // Assets save IMMEDIATELY — the ephemeral contract can't hold a file — so
  // this posts the file on its own, swaps the region for the server's
  // re-render of it, and triggers a preview render. The input is unnamed,
  // keeping file bytes out of every render/Save post.
  async uploadAsset(event) {
    const input = event.target
    const file = input.files[0]
    if (!file) return

    const region = input.closest("[data-upload-region]")
    const body = new FormData()
    body.append("file", file)
    body.append("kind", event.params.kind)
    body.append("branding_profile_id", this.formTarget.querySelector("input[name='branding_profile_id']").value)
    input.value = ""

    this.status("Uploading…", { busy: true })
    try {
      const response = await fetch(this.uploadUrlValue, {
        method: "POST",
        body,
        headers: { "X-CSRF-Token": this.csrfToken() }
      })
      const html = await response.text()
      if (region && html) region.innerHTML = html
      if (!response.ok) throw new Error(`upload failed (${response.status})`)

      this.changed()
    } catch {
      this.status("Upload failed", { error: true })
    }
  }

  retry() {
    this.render()
  }

  // The theme designer's kebab "Rename" (TASK-232.4, decision-60 — replaces
  // the old scope bar + Save as…/Change parent dialogs entirely): focuses
  // and selects the top bar's own name input rather than opening anything.
  focusName(event) {
    if (event) event.preventDefault()
    if (!this.hasNameInputTarget) return

    this.nameInputTarget.focus()
    this.nameInputTarget.select()
  }

  // Team-scope source search (TASK-134.1): swap the dialog's rows for the
  // server's matches. Injected radios carry the same Stimulus actions, so a
  // pick still closes the dialog and triggers a render.
  searchSources(event) {
    clearTimeout(this.sourceTimer)
    const q = event.target.value
    this.sourceTimer = setTimeout(async () => {
      try {
        const url = new URL(this.sourcesUrlValue, window.location.origin)
        if (q) url.searchParams.set("q", q)
        const selected = this.formTarget.querySelector("input[name='submission_id']:checked")
        if (selected) url.searchParams.set("selected_id", selected.value)
        const response = await fetch(url, { headers: { Accept: "text/html" } })
        if (response.ok) this.sourceListTarget.innerHTML = await response.text()
      } catch { /* keep the current rows on a failed search */ }
    }, 300)
  }

  // The Content group's form picker (TASK-225.4) — same "navigate, the
  // server re-renders" pattern used throughout this controller: which form's
  // rules are being
  // edited decides a whole sub-tree of server-rendered controls (sections,
  // fields), so this is a page load rather than a client-side swap. Any
  // other query param already on the URL (submission_id, branding_profile_id)
  // rides along untouched; the server decides whether the preview source
  // needs to follow the new form.
  changeContentForm(event) {
    const url = new URL(window.location.href)
    url.searchParams.set("form_definition_id", event.target.value)
    window.location.assign(url.toString())
  }

  // The sub-bar's Theme ▾ at team level (TASK-232.5, doc-35 "Contents
  // designer") — preview-only, never writes: navigates like
  // changeContentForm/changeLayout so the server re-resolves the canvas
  // through the picked theme while the per-form rule keeps resolving
  // through the team (contents are team-wide, decision-60 §3). The project
  // host's own Theme ▾ is a plain auto-submitting form instead (it writes
  // the project's actual pick) and never calls this action.
  // Project host: the sub-bar's Theme select writes the project's pick
  // through the standalone #project-theme-pick form (a sibling of the
  // studio form — see show.html.erb), reached here because the select is
  // not inside that form and so cannot carry an auto-submit action itself.
  submitThemePick() {
    if (this.hasThemePickFormTarget) this.themePickFormTarget.requestSubmit()
  }

  changeTheme(event) {
    const url = new URL(window.location.href)
    url.searchParams.set("theme_id", event.target.value)
    window.location.assign(url.toString())
  }

  // The Layout select (TASK-229.5) — same "navigate, the server re-renders"
  // pattern as changeContentForm: which source (main or a layout) the rail
  // edits decides a whole sub-tree of server-rendered controls.
  changeLayout(event) {
    const url = new URL(window.location.href)
    url.searchParams.set("layout_id", event.target.value)
    window.location.assign(url.toString())
  }

  // The Columns accordion's per-group All/None links (TASK-229.5).
  checkColumnGroup(event) {
    this.setColumnGroup(event, true)
  }

  uncheckColumnGroup(event) {
    this.setColumnGroup(event, false)
  }

  setColumnGroup(event, checked) {
    const group = this.columnGroupTargets.find((el) => el.dataset.group === event.currentTarget.dataset.group)
    if (!group) return

    group.querySelectorAll('input[type="checkbox"]').forEach((box) => { box.checked = checked })
    this.changed()
  }

  // At least one of Excel/CSV stays on (AC #4) — reverts the box that would
  // have left the pair empty and shows a brief hint, client-side; the model
  // validation is the real guard (SpreadsheetLayout / ReportDesignFormRule),
  // this only saves a round trip for the obvious case. Bound directly on
  // each checkbox, so it runs (target phase) before the rail wrapper's own
  // delegated `changed` (bubble phase) sees the corrected state.
  guardFormats(event) {
    const group = event.currentTarget.closest("[data-formats-group]")
    const anyChecked = Array.from(group.querySelectorAll('input[type="checkbox"]')).some((box) => box.checked)

    if (!anyChecked) {
      event.currentTarget.checked = true
      if (this.hasFormatsErrorTarget) {
        this.formatsErrorTarget.classList.remove("hidden")
        clearTimeout(this.formatsErrorTimer)
        this.formatsErrorTimer = setTimeout(() => this.formatsErrorTarget.classList.add("hidden"), 2000)
      }
    }
  }

  // Hovering/focusing a Columns row highlights that column throughout the
  // sheet preview (doc-33 "Preview") — every header/body cell for the SAME
  // key carries the identical data-column-key, rendered by
  // _sheet_preview_table, so this lights up the whole column at once.
  highlightColumn(event) {
    this.toggleColumnHighlight(event, true)
  }

  unhighlightColumn(event) {
    this.toggleColumnHighlight(event, false)
  }

  toggleColumnHighlight(event, on) {
    if (!this.hasSheetPreviewTarget) return

    const key = event.currentTarget.dataset.columnKey
    if (!key) return

    this.sheetPreviewTarget.querySelectorAll(`[data-column-key="${CSS.escape(key)}"]`).forEach((cell) => {
      cell.classList.toggle("bg-blue-100", on)
    })
  }

  async render() {
    if (this.previewKindValue === "sheet") return this.renderSheet()
    if (!this.hasFrameTarget) return

    clearTimeout(this.timer)
    this.abortController?.abort()
    this.abortController = new AbortController()

    this.status("Rendering…", { busy: true })
    this.frameTarget.classList.add("opacity-50")
    if (this.hasRetryTarget) this.retryTarget.classList.add("hidden")

    try {
      const response = await fetch(this.renderUrlValue, {
        method: "POST",
        body: new FormData(this.formTarget),
        headers: { "X-CSRF-Token": this.csrfToken() },
        signal: this.abortController.signal
      })
      if (!response.ok) throw new Error(`render failed (${response.status})`)

      const blob = await response.blob()
      const previous = this.objectUrl
      this.objectUrl = URL.createObjectURL(blob)
      this.frameTarget.src = this.objectUrl
      // Revoke AFTER the iframe has had a moment to consume the old URL —
      // revoking immediately blanks the page it is still displaying.
      if (previous) setTimeout(() => URL.revokeObjectURL(previous), 1000)

      this.frameTarget.classList.remove("opacity-50")
      this.status("Up to date")
    } catch (error) {
      if (error.name === "AbortError") return

      // The last good page stays visible; only the chip changes.
      this.frameTarget.classList.remove("opacity-50")
      this.status("Couldn't render", { error: true })
      if (this.hasRetryTarget) this.retryTarget.classList.remove("hidden")
    }
  }

  // Spreadsheet mode's live sample (TASK-229.5) — same debounce/abort/status
  // machinery as #render(), an HTML fragment swapped into sheetPreviewTarget
  // instead of a PDF blob into the iframe (a spreadsheet has no page).
  async renderSheet() {
    if (!this.hasSheetPreviewTarget) return

    clearTimeout(this.timer)
    this.abortController?.abort()
    this.abortController = new AbortController()

    this.status("Rendering…", { busy: true })

    try {
      const response = await fetch(this.sheetPreviewUrlValue, {
        method: "POST",
        body: new FormData(this.formTarget),
        headers: { "X-CSRF-Token": this.csrfToken(), Accept: "text/html" },
        signal: this.abortController.signal
      })
      if (!response.ok) throw new Error(`sheet preview failed (${response.status})`)

      this.sheetPreviewTarget.innerHTML = await response.text()
      this.status("Up to date")
    } catch (error) {
      if (error.name === "AbortError") return

      this.status("Couldn't render", { error: true })
    }
  }

  status(text, { busy = false, error = false } = {}) {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = text
    this.statusTarget.classList.toggle("text-amber-700", busy)
    this.statusTarget.classList.toggle("border-amber-400", busy)
    this.statusTarget.classList.toggle("text-red-700", error)
    this.statusTarget.classList.toggle("border-red-400", error)
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }
}
